import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useEvent } from "../context/EventProvider";
import { useAccessLog } from "../log/actionLog";
import { Icon } from "../ui/Icon";
import { useAnswers } from "../funnel/answers";
import { stamped } from "../funnel/answerSession";
import { useSessionKey } from "../auth/useSessionKey";
import { useLeaveGuard } from "../funnel/leaveGuard";
import { SaveNote, useStepSave } from "../funnel/stepSave";
import { standOf, type StandIn } from "../funnel/participant";
import { useParticipation } from "../funnel/useParticipation";
import { stepAfter } from "../funnel/thread";
import { doorNow } from "../funnel/cloture";
import { useOffer } from "../funnel/useOffre";
import { defaultOnOf, fallbackOf } from "../settings/catalog";
import { useFlagOr, useSettings, useT, useText } from "../settings/context";
import { Button } from "../ui/Button";
import { withContext } from "./registry";
import { Prose } from "../ui/Prose";
import { InPlace } from "../panel/InPlace";

/**
 * REGISTRATION, STEP 1 — how are you taking part?
 *
 * Ported from the prototype rather than approximated. Everything below is one of
 * its behaviours, and each exists because leaving it out produces a screen that
 * lies:
 *
 *   — CLOSED REGISTRATION is an honest lock: it says so instead of showing a form
 *     that will fail at the end. Closed by hand, or once the deadline the organiser
 *     set has passed — the same instant for everyone, wherever they read it from
 *     (see `funnel/cloture.ts`).
 *   — REGISTRATION NOT OPEN YET is the same lock, read the other way round, and it
 *     gets its OWN message: somebody arriving the day before is told to come back,
 *     not that they have missed it.
 *   — AUTOMATIC REGISTRATION does not ask the question at all; it states the
 *     outcome, and the step leaves the thread (see `funnel/thread.ts`).
 *   — AN ANSWER SWITCHED OFF WHILE IT WAS CHOSEN falls back to the first one still
 *     offered. A chosen state that no longer exists would lie everywhere
 *     downstream.
 *   — ALL ANSWERS SWITCHED OFF gets a guard, not a mute screen: the organiser has
 *     cut every way to answer, and only they can put one back.
 *   — DECLINING can be asked to explain itself, optionally or not.
 *   — BEING REPRESENTED designates the stand-in HERE. That is the product's
 *     "Invitation" step folded into this one: one screen fewer, and the question
 *     asked at the moment it arises.
 *
 * Displayed strings go through `t()`, French kept at the call site as the
 * fallback — EXCEPT the messages addressed to the ORGANISER (an empty catalog,
 * a step removed from the thread), French like the rest of the panel.
 */

/** The four answers, in the prototype's order, with its icons and wording. */
const ANSWERS = [
  {
    value: "onsite",
    icon: "onsite",
    show: "FO_MEETING_V2_REGISTRATION_ONSITE_SHOW",
    label: "FO_MEETING_V2_REGISTRATION_ONSITE_LABEL",
    /** No fixed note: this answer says WHERE, from the venue setting. */
    noteKey: null,
    note: null,
  },
  {
    value: "remote",
    icon: "remote",
    show: "FO_MEETING_V2_REGISTRATION_REMOTE_SHOW",
    label: "FO_MEETING_V2_REGISTRATION_REMOTE_LABEL",
    noteKey: "LIVE_STREAMED",
    note: "Diffusion en direct",
  },
  {
    value: "represented",
    icon: "represented",
    show: "FO_MEETING_V2_REGISTRATION_REPRESENTED_SHOW",
    label: "FO_MEETING_V2_REGISTRATION_REPRESENTED_LABEL",
    noteKey: "A_COLLEAGUE_WILL_COME",
    note: "Un collègue viendra à votre place",
  },
  {
    value: "decline",
    icon: "decline",
    show: "FO_MEETING_V2_REGISTRATION_DECLINE_SHOW",
    label: "FO_MEETING_V2_REGISTRATION_DECLINE_LABEL",
    noteKey: "KEPT_INFORMED_BY_EMAIL",
    note: "Vous resterez informé par email",
  },
] as const;

type Answer = (typeof ANSWERS)[number];

function Option({
  answer,
  chosen,
  onPick,
}: {
  answer: Answer;
  chosen: boolean;
  onPick: () => void;
}) {
  const t = useT();
  const label = useText(answer.label, fallbackOf(answer.label));
  const venue = useText("FO_MEETING_V2_EVENT_PLACE");
  // A multi-line address would break the card's rhythm — the footer is where it
  // gets its own lines.
  const note =
    answer.noteKey && answer.note
      ? t(answer.noteKey, answer.note)
      : venue.replace(/\s*\n+\s*/g, " · ");

  return (
    <li>
      <button
        type="button"
        className={`opt${chosen ? " on" : ""}`}
        aria-pressed={chosen}
        onClick={onPick}
      >
        <span className="opt__i" aria-hidden="true">
          <Icon name={answer.icon} />
        </span>
        <span className="opt__t">
          <b>{label}</b>
          {note && <small>{note}</small>}
        </span>
      </button>
    </li>
  );
}

export function Registration({ asPage }: { asPage?: true }) {
  const t = useT();
  const { value } = useSettings();
  const offer = useOffer();

  const title = useText(
    "FO_MEETING_V2_REGISTRATION_TITLE",
    fallbackOf("FO_MEETING_V2_REGISTRATION_TITLE"),
  );
  const intro = useText(
    "FO_MEETING_V2_REGISTRATION_INTRO",
    fallbackOf("FO_MEETING_V2_REGISTRATION_INTRO"),
  );
  const html = useText("FO_MEETING_V2_REGISTRATION_HTML");
  const submitLabel = useText(
    "FO_MEETING_V2_REGISTRATION_SUBMIT",
    fallbackOf("FO_MEETING_V2_REGISTRATION_SUBMIT"),
  );

  /**
   * LA PORTE — PAS ENCORE OUVERTE, OUVERTE, OU REFERMÉE.
   *
   * ⚠️ PAR LA MÊME FONCTION QUE LE FIL (`funnel/cloture.ts`), et non par une seconde
   * lecture des réglages. Le fil s'arrête à cet écran dès qu'une des deux portes est
   * fermée ; un écran qui, lui, continuerait d'offrir le formulaire enverrait
   * s'inscrire dans un parcours déjà clos — un « Continuer » qui ne mène nulle part.
   *
   * ⚠️ ET L'ÉCRAN DIT LAQUELLE DES DEUX. Un seul message pour les deux états aurait
   * annoncé « inscriptions closes » à qui arrive la veille de l'ouverture : il ne
   * serait jamais revenu, alors qu'il n'avait qu'à attendre un jour.
   *
   * ⚠️ L'HEURE EST LUE AU DESSIN DE L'ÉCRAN. Un onglet ouvert AVANT l'heure garde
   * son formulaire : lui arracher une saisie à moitié faite ne garantirait rien de plus
   * — l'enregistrement ne passe pas par ce réglage — et lui coûterait ce qu'il vient de
   * taper. La porte est close pour qui arrive après, ce qui est la question posée.
   */
  const door = doorNow(value);
  const locked = door !== "open";
  const closedText = useText(
    "FO_MEETING_V2_REGISTRATION_CLOSED_TEXT",
    fallbackOf("FO_MEETING_V2_REGISTRATION_CLOSED_TEXT"),
  );
  const waitingText = useText(
    "FO_MEETING_V2_REGISTRATION_NOT_OPEN_TEXT",
    fallbackOf("FO_MEETING_V2_REGISTRATION_NOT_OPEN_TEXT"),
  );
  const automatic = useFlagOr("FO_MEETING_V2_REGISTRATION_AUTO");
  const auto = automatic && !locked;

  const askReason = useFlagOr("FO_MEETING_V2_REGISTRATION_REASON_ASK");
  const reasonLabel = useText(
    "FO_MEETING_V2_REGISTRATION_REASON_LABEL",
    fallbackOf("FO_MEETING_V2_REGISTRATION_REASON_LABEL"),
  );
  const reasonRequired = useFlagOr(
    "FO_MEETING_V2_REGISTRATION_REASON_REQUIRED",
  );

  // The answers still offered — read through the SAME rule the panel uses, so a
  // switch nobody has touched means the same thing on both sides.
  const offered = ANSWERS.filter((a) => {
    const raw = value(a.show);
    return typeof raw === "boolean" ? raw : defaultOnOf(a.show);
  });

  /**
   * THREE SOURCES, IN THIS ORDER: what is being answered right now, then what the
   * server already holds, then nothing.
   *
   * The middle one is why a step reopened later shows the choice already made. It
   * arrives asynchronously, so the fields are DERIVED rather than seeded into
   * state — a `useState` initialiser runs once, before the answer is back, and the
   * form would stay empty for somebody who had already filled it in.
   */
  const [answers, record] = useAnswers();
  const session = useSessionKey();
  /**
   * `fresh` : l'entrée du tunnel (menu « Inscription ») relit la participation à
   * chaque montage. Sans cela, une participation supprimée depuis le BO restait
   * affichée (étapes cochées, choix faits) tant que l'onglet ne perdait pas le focus,
   * car le cache est en `staleTime: Infinity`. Voir `participationGone.ts`.
   */
  const { recorded } = useParticipation({ fresh: true });
  const [typed, setTyped] = useState<string | null>(null);
  /**
   * ⚠️ ET LE REMPLAÇANT AUSSI — il était le seul champ encore SEMÉ dans l'état, en
   * contradiction avec la règle écrite juste au-dessus. Conséquence : « Modifier mon
   * inscription » ouvrait les trois champs VIDES sur un remplaçant déjà désigné, et un
   * « Enregistrer » sans rien changer l'effaçait de la fiche. Voir `standOf`.
   */
  const [typedStand, setTypedStand] = useState<StandIn | null>(null);
  const stand = typedStand ??
    answers.stand ??
    standOf(recorded?.extended) ?? { first: "", last: "", email: "" };
  const reason = typed ?? answers.reason ?? recorded?.reason ?? "";
  const picked = answers.participation ?? recorded?.answer ?? null;

  // An answer cut while it was chosen falls back to the first still offered.
  const chosen =
    picked && offered.some((a) => a.value === picked)
      ? picked
      : (offered[0]?.value ?? null);

  const none = offered.length === 0 && !locked;
  const navigate = useNavigate();
  const event = useEvent();
  // « Ma participation » se lit comme la confirmation ; l'étape du tunnel, non.
  useAccessLog("confirmationAccess", !!asPage);

  const next = stepAfter(value, "/registration", offer);

  /**
   * ⚠️ « REPRÉSENTÉ » SANS PERSONNE À NOMMER NE DIT RIEN, et c'est devenu urgent le jour
   * où cette réponse a mené droit à la confirmation : on pouvait répondre « un collègue
   * viendra à ma place » et valider dans la seconde, en laissant l'organisateur devant
   * une place réservée pour personne — sans savoir qui attendre ni qui relancer.
   *
   * ⚠️ LE NOM SUFFIT, PAS L'ADRESSE — la même règle que les accompagnants, et pour la
   * même raison : le produit accepte parfaitement quelqu'un sans compte, qui reçoit son
   * badge à l'accueil. Exiger une adresse fermerait la porte à un cas courant.
   */
  const names = stand.first.trim() !== "" || stand.last.trim() !== "";
  /**
   * ⚠️ CE QUI MANQUE À LA RÉPONSE, SÉPARÉ DE CE QUI MANQUE AU PARCOURS. Les deux étaient
   * une seule condition, et « Enregistrer » l'aurait héritée : une réponse complète
   * n'aurait pas pu s'écrire sur une étape sans suivante (la participation retirée du
   * fil, ou servie en page). Ce qu'on enregistre dépend de la réponse ; où l'on va
   * dépend du fil.
   */
  const incomplete =
    !chosen ||
    (chosen === "decline" && askReason && reasonRequired && !reason.trim()) ||
    (chosen === "represented" && !names);
  const blocked = incomplete || !next;

  const { correction, ready, state, save, touched, removal } = useStepSave({ asPage });
  /* Le garde que les autres étapes tiennent de `useFunnelNav` : celle-ci navigue seule. */
  useLeaveGuard();
  /**
   * ═══ « ENREGISTRER » CORRIGE UNE INSCRIPTION, IL N'EN FAIT PAS (bug #522) ═══
   *
   * ⚠️ MÊME EN PAGE. La garde laisse entrer ici un refus (voir `funnel/StepPage.tsx`) :
   * c'est par cette page qu'on revient sur « je ne viens pas ». Son seul bouton écrivait
   * alors la nouvelle réponse telle quelle, et « à distance » inscrivait sans profil,
   * sans formulaire, sans société, à tous les jours. Sans inscription, la page mène donc
   * au parcours comme l'étape 1, réponse en main : « Continuer », et tout se refait.
   *
   * ⚠️ ET SEULEMENT EN PAGE (tâche #546) : dans le tunnel, même en correction, on n'offre
   * que « Continuer ». Voir l'étape dates, où la règle est écrite.
   */
  const saveable = asPage && correction;
  const answered = { participation: chosen ?? "", reason, stand };

  /* Le remplaçant se saisit en trois champs, et chacun retire le mot « enregistré » :
     l'écrire une fois ici évite de le redire trois fois plus bas. */
  const nameOf = (patch: Partial<typeof stand>) => {
    touched();
    setTypedStand({ ...stand, ...patch });
  };

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (blocked || !next) return;
    // Answers travel in the navigation state — see `funnel/answers.ts`.
    navigate(withContext(next.path, event?.token), {
      state: stamped({ ...answers, ...answered }, session),
    });
  };

  return (
    <section className="page">
      <div className="ttl" data-cfg="registrationTitle">
        <InPlace settingKey="FO_MEETING_V2_REGISTRATION_TITLE" text={title} as="h2" />
        <InPlace settingKey="FO_MEETING_V2_REGISTRATION_INTRO" text={intro} as="p" />
      </div>

      {/* Closed: say why. A form that cannot succeed costs more than a locked door. */}
      <div
        className="alert"
        data-cfg="registrationClosing"
        hidden={door !== "closed"}
      >
        <span className="alert__d" aria-hidden="true">
          <i />
        </span>
        <span>
          <b>{t("REGISTRATIONS_CLOSED", 'Inscriptions closes')}</b>
          <small>{closedText}</small>
        </span>
      </div>

      {/**
       * Pas encore ouvertes : la même porte fermée, mais on reviendra.
       *
       * ⚠️ SON PROPRE BLOC, ET NON LE PRÉCÉDENT AVEC UN AUTRE TEXTE. Ce sont deux
       * messages que l'organisateur écrit séparément, et deux blocs qu'il habille
       * séparément (`data-cfg`) : partagés, personnaliser l'un aurait repeint l'autre,
       * et la carte du panneau aurait proposé le message de clôture devant un écran qui
       * affiche celui d'attente.
       */}
      <div
        className="alert"
        data-cfg="registrationOpening"
        hidden={door !== "not-yet"}
      >
        <span className="alert__d" aria-hidden="true">
          <i />
        </span>
        <span>
          <b>{t("REGISTRATIONS_NOT_OPEN_YET", "Inscriptions à venir")}</b>
          <small>{waitingText}</small>
        </span>
      </div>

      {/* The organiser's own introduction, written as HTML in the panel. It is
          THEIR content rendered as they wrote it — the same trust the product
          already grants them on its information pages. */}
      {!locked && html.trim() !== "" && (
        <Prose
          html={html}
          className="se-prose"
          data-cfg="registrationTitle"
          />
      )}

      <p className="hint" data-cfg="registrationOptions" hidden={!auto}>
        Inscription automatique active : la question n'est pas posée, chacun est
        inscrit en présentiel dès sa connexion.
      </p>

      {/* Every answer switched off. Only the organiser can put one back, so the
          message is addressed to them rather than left as an empty screen. */}
      <div className="alert" data-cfg="registrationOptions" hidden={!none}>
        <span className="alert__d" aria-hidden="true">
          <i />
        </span>
        <span>
          <b>Aucune réponse proposée</b>
          <small>
            Réactivez au moins une réponse de participation pour que l'étape ait
            un sens.
          </small>
        </span>
      </div>

      <form className="form" onSubmit={submit}>
        <ul
          className="opts f--wide"
          data-cfg="registrationOptions"
          hidden={locked || none || auto}
        >
          {offered.map((answer) => (
            <Option
              key={answer.value}
              answer={answer}
              chosen={chosen === answer.value}
              onPick={() => {
                // « C'est enregistré » ne doit pas rester affiché au-dessus d'une
                // réponse qu'on vient de changer depuis.
                touched();
                record({ participation: answer.value });
              }}
            />
          ))}
        </ul>

        {/* Asked of whoever is not coming, and only then. */}
        <div
          className="f f--wide"
          data-cfg="registrationOptions"
          hidden={locked || chosen !== "decline" || !askReason}
        >
          <label htmlFor="reg-reason">
            {reasonLabel}
            {reasonRequired && <i> *</i>}
          </label>
          <input
            id="reg-reason"
            type="text"
            autoComplete="off"
            value={reason}
            onChange={(e) => {
              touched();
              setTyped(e.target.value);
            }}
          />
        </div>

        {/* The stand-in, designated HERE: the product's "Invitation" step folded
            into this one. */}
        <div
          className="form f--wide"
          data-cfg="registrationOptions"
          hidden={locked || chosen !== "represented"}
        >
          {/* ⚠️ LES LARGEURS SONT ÉCRITES ICI, et il le faut. Ces trois champs ne
              viennent d'aucun formulaire du back office : personne ne peut leur régler
              une taille depuis le panneau. Sans ces classes ils vaudraient « pleine »
              par défaut et tomberaient en UNE colonne, alors que la grille d'avant les
              mettait deux par ligne sur grand écran. Un nom et un prénom côte à côte,
              l'adresse en dessous : c'est la forme que ce bloc avait. */}
          <div className="f f--moyen">
            <label htmlFor="reg-stand-first">{t("STAND_IN_FIRST_NAME", 'Prénom du représentant')}</label>
            <input
              id="reg-stand-first"
              type="text"
              autoComplete="off"
              value={stand.first}
              onChange={(e) => nameOf({ first: e.target.value })}
            />
          </div>
          <div className="f f--moyen">
            <label htmlFor="reg-stand-last">{t("STAND_IN_LAST_NAME", 'Nom du représentant')}</label>
            <input
              id="reg-stand-last"
              type="text"
              autoComplete="off"
              value={stand.last}
              onChange={(e) => nameOf({ last: e.target.value })}
            />
          </div>
          <div className="f f--grand">
            <label htmlFor="reg-stand-email">{t("THEIR_EMAIL", 'Son adresse email')}</label>
            <input
              id="reg-stand-email"
              type="email"
              autoComplete="off"
              placeholder={t(
                "THEY_GET_BADGE_AND_ACCESS",
                "Il recevra le badge et les accès",
              )}
              value={stand.email}
              onChange={(e) => nameOf({ email: e.target.value })}
            />
          </div>

          {/* ⚠️ UN BOUTON GRISÉ SANS UN MOT EST UN CUL-DE-SAC. « Continuer » attend un
              nom (voir `nomme`) ; sans cette ligne, on cherche ce qui cloche dans les
              trois champs, dont deux sont facultatifs. Elle ne s'affiche que tant qu'il
              manque quelque chose — une consigne qui reste après avoir été suivie se lit
              comme un reproche. */}
          <p className="hint f--wide" hidden={names}>
            {t(
              "STAND_IN_NAME_REQUIRED",
              "Indiquez au moins son nom : c'est lui qui recevra le badge, et l'organisateur doit savoir qui attendre.",
            )}
          </p>
        </div>

        {/**
         * ⚠️ `.actions`, COMME PARTOUT AILLEURS DANS LE TUNNEL. Cette étape portait son
         * bouton dans un simple bloc de formulaire : en bureau, il restait donc collé à
         * GAUCHE sous les réponses, quand « Continuer » se tient à droite sur toutes les
         * autres étapes. Une cible qui se déplace d'un écran à l'autre se cherche à
         * chaque fois.
         *
         * ⚠️ ET `f--wide` RESTE : le formulaire est une grille à deux colonnes en bureau,
         * et sans cette classe la rangée d'actions n'occuperait que la première.
         */}
        <SaveNote state={state} removal={removal} />

        {/* Dans le tunnel « Continuer », en page « Enregistrer » une fois inscrit,
            jamais les deux. Voir l'étape dates, où la règle est écrite. */}
        <div className="actions f--wide" hidden={locked || none}>
          {asPage && (
            <Button
              type="button"
              variant="ghost"
              onClick={() => navigate(withContext("/", event?.token))}
            >
              {t("BACK_TO_HOME", "Retour à l'accueil")}
            </Button>
          )}
          {saveable && (
            <Button
              type="button"
              data-cfg="registrationButton"
              loading={state === "saving"}
              disabled={incomplete || !ready}
              onClick={() => save(answered)}
            >
              {t("SAVE", "Enregistrer")}
            </Button>
          )}
          {!saveable && (
            <Button
              type="submit"
              data-cfg="registrationButton"
              disabled={blocked}
            >
              {submitLabel}
            </Button>
          )}
        </div>
      </form>
    </section>
  );
}
